// SEQ-193 follow-on: "P&L month on month" on the Commercial Dashboard.
//
// Reuses, rather than copies: the P&L Analytics month on month table
// (PnlMonthOnMonthTable), the request planner (buildPnlRequestPlan) and the
// data hook (usePnlDashboardData). This file only supplies the Dashboard's
// filters and the card around them. Defaults and the filter mapping are
// documented in features/pnlAnalytics/dashboardMonthOnMonth.ts.
//
// ACCESS. The outer component renders nothing, and mounts nothing that
// fetches, unless the viewer has can_view_financials (the flag that gates the
// P&L Analytics route and sidebar link). The data hooks live in the inner
// component, so a viewer without access makes no P&L request at all.
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { useAuth } from "../../hooks/useAuth";
import { useSafeMode } from "../../contexts/SafeModeContext";
import { useDashboardFilter } from "../../contexts/useDashboardFilterHooks";
import { FinancialAccessGate } from "../../features/pnlAnalytics/FinancialAccessGate";
import {
  buildDashboardMonthOnMonthConfig,
  mapDashboardProjects,
  resolveDashboardMomRange,
} from "../../features/pnlAnalytics/dashboardMonthOnMonth";
import { buildPnlRequestPlan, type PnlDashboardPageFilters } from "../../features/pnlAnalytics/dashboardRequestKeys";
import { usePnlDashboardData } from "../../features/pnlAnalytics/usePnlDashboardData";
import { usePnlMetricRegistry } from "../../features/pnlAnalytics/usePnlMetricRegistry";
import { buildKeyViews, EMPTY_KEY_VIEW, type PnlProjectNameSource } from "../../features/pnlAnalytics/widgetDataset";
import type { PnlAnalyticsQueryFilters } from "../../features/pnlAnalytics/usePnlAnalytics";
import { PnlMonthOnMonthTable } from "../pnlAnalytics/PnlMonthOnMonthTable";
import { HowWorkedOut } from "../howWorkedOut/HowWorkedOut";
import {
  DASHBOARD_MOM_DESCRIBE_CONTEXT,
  DASHBOARD_MOM_FIGURES,
} from "../../features/pnlAnalytics/dashboardMonthOnMonth.metrics";

// Reserved so the card does not change height between loading, empty and
// loaded (a one row table plus its header sits inside this).
const BODY_MIN_HEIGHT = 168;

function BodyMessage({ children, role }: { children: React.ReactNode; role?: "status" | "alert" }) {
  return (
    <div
      role={role}
      style={{
        minHeight: BODY_MIN_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        color: "var(--text-secondary)",
        fontSize: "0.9rem",
      }}
    >
      <span>{children}</span>
    </div>
  );
}

function BodySkeleton() {
  return (
    <div role="status" aria-label="Loading P&L month on month" style={{ minHeight: BODY_MIN_HEIGHT, display: "flex", flexDirection: "column", gap: 12 }}>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            height: 28,
            borderRadius: 8,
            background: "var(--bg-tertiary)",
          }}
        />
      ))}
    </div>
  );
}

function DashboardPnlMonthOnMonthBody({ projects }: { projects: PnlProjectNameSource[] }) {
  // Re-render when Safe Mode flips: the table's formatter reads a module flag
  // the provider sets during its own render, so this subscription is what
  // makes the figures re-mask.
  useSafeMode();
  const { filters, effectiveProjectIds } = useDashboardFilter();
  const registry = usePnlMetricRegistry();

  const todayIso = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const config = useMemo(() => buildDashboardMonthOnMonthConfig(), []);

  const range = useMemo(
    () => resolveDashboardMomRange({ datePreset: filters.datePreset, dateRange: filters.dateRange }, todayIso),
    [filters.datePreset, filters.dateRange, todayIso],
  );
  const projectMap = useMemo(() => mapDashboardProjects(effectiveProjectIds), [effectiveProjectIds]);
  const projectIds = useMemo(() => (projectMap.kind === "some" ? projectMap.projectIds : []), [projectMap]);

  const canQuery = range !== null && projectMap.kind !== "none";

  const pageFilters = useMemo<PnlDashboardPageFilters>(
    () => ({ range: { from: range?.from ?? null, to: range?.to ?? null }, projectIds, statuses: [] }),
    [range, projectIds],
  );
  const queryFilters = useMemo<PnlAnalyticsQueryFilters>(
    () => ({ from: range?.from ?? null, to: range?.to ?? null, projectIds, statuses: [] }),
    [range, projectIds],
  );

  // An empty plan makes usePnlDashboardData skip the call entirely.
  const plan = useMemo(
    () =>
      canQuery
        ? buildPnlRequestPlan([config], pageFilters, [], todayIso, registry.metrics)
        : { keys: [], keyByWidgetId: {}, comparisonKeyByWidgetId: {}, capExceeded: false, droppedWidgetIds: [] },
    [canQuery, config, pageFilters, todayIso, registry.metrics],
  );
  const dataset = usePnlDashboardData(plan);
  const views = useMemo(() => buildKeyViews(plan.keys, dataset.byKey, projects), [plan.keys, dataset.byKey, projects]);

  let body: React.ReactNode;
  if (projectMap.kind === "none") {
    body = <BodyMessage>No projects match the filters above, so there is no P&L to compare.</BodyMessage>;
  } else if (range === null) {
    body = <BodyMessage>The date range above has no complete month yet. Pick an earlier range to compare months.</BodyMessage>;
  } else if (registry.loading || dataset.loading) {
    body = <BodySkeleton />;
  } else if (dataset.error) {
    body = (
      <BodyMessage role="alert">
        P&amp;L could not be loaded.{" "}
        <button type="button" className="btn btn-ghost" onClick={dataset.refresh}>
          Retry
        </button>
      </BodyMessage>
    );
  } else {
    const key = plan.keyByWidgetId[config.widgetId];
    const view = (key ? views[key] : undefined) ?? EMPTY_KEY_VIEW;
    body = (
      <PnlMonthOnMonthTable
        config={config}
        view={view}
        metrics={registry.metrics}
        queryFilters={queryFilters}
        projects={projects}
        describeContext={DASHBOARD_MOM_DESCRIBE_CONTEXT}
      />
    );
  }

  return (
    <div data-tour-id="pnl-month-on-month" style={{ marginBottom: "var(--spacing-xl)" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.25rem" }}>
        <div className="hwo-heading">
          <h3 style={{ fontSize: "1.1rem", color: "var(--text-primary)", margin: 0 }}>P&amp;L month on month</h3>
          <HowWorkedOut definition={DASHBOARD_MOM_FIGURES["pnl-month-on-month"]} />
        </div>
        <Link to="/pnl-analytics" style={{ fontSize: "0.85rem" }}>
          Open in P&amp;L Analytics
        </Link>
      </div>
      <p style={{ fontSize: "0.85rem", color: "var(--text-secondary)", margin: "0 0 1rem" }}>
        Net profit for each complete month, with the change between the last two. Follows the filters above.
      </p>
      <div className="card" style={{ padding: "1rem" }}>
        {body}
      </div>
    </div>
  );
}

export function DashboardPnlMonthOnMonth({ projects }: { projects: PnlProjectNameSource[] }) {
  const { profile, loading } = useAuth();
  return (
    <FinancialAccessGate profile={profile} loading={loading}>
      <DashboardPnlMonthOnMonthBody projects={projects} />
    </FinancialAccessGate>
  );
}
